import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Project, Team } from '@graphql/generated';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';

type WebDrawerNavigatorContext = {
  /** Web Drawer Navigator Header title */
  headerTitle?: string;
  setHeaderTitle: (header: WebDrawerNavigatorContext['headerTitle']) => void;
  selectedProject?: Project;
  setSelectedProject: (
    project: WebDrawerNavigatorContext['selectedProject']
  ) => void;
  selectedTeam?: Team;
  setSelectedTeam: (team: WebDrawerNavigatorContext['selectedTeam']) => void;
  showProjectColorAndIconModal?: boolean;
  setShowProjectColorAndIconModal: (
    show: WebDrawerNavigatorContext['showProjectColorAndIconModal']
  ) => void;

  showPending?: boolean;
  setShowPending: (
    showPending: WebDrawerNavigatorContext['showPending']
  ) => void;
};

const webDrawerNavigatorContext = createContext<WebDrawerNavigatorContext>({
  setHeaderTitle: () => void 0,
  setSelectedProject: () => void 0,
  setShowProjectColorAndIconModal: () => void 0,
  setSelectedTeam: () => void 0,
  setShowPending: () => void 0,
});

type Props = {
  children: React.ReactNode;
};

export const WebDrawerNavigatorProvider = ({ children }: Props) => {
  const { Provider } = webDrawerNavigatorContext;
  const { mixpanel, trackEvent } = useMixpanel();

  const [headerTitle, setHeaderTitle] = useState<string | undefined>();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [selectedTeam, setSelectedTeamState] = useState<Team | undefined>();
  const [showPending, setShowPending] = useState<boolean | false>();
  const [showProjectColorAndIconModal, setShowProjectColorAndIconModal] =
    useState<boolean | undefined>();

  const selectedTeamRef = useRef(selectedTeam);
  selectedTeamRef.current = selectedTeam;

  const setSelectedTeam = useCallback(
    (team: WebDrawerNavigatorContext['selectedTeam']) => {
      if (selectedTeamRef.current?.id !== team?.id) {
        trackEvent(MIXPANEL_EVENTS.SWITCHED_TEAM, { new_team_id: team?.id });
      }
      setSelectedTeamState(team);
    },
    [trackEvent]
  );

  useEffect(() => {
    if (mixpanel) {
      const props: Record<string, any> = {};
      if (selectedTeam?.id) {
        props.team_id = selectedTeam.id;
      }
      if (selectedProject?.id) {
        props.project_id = selectedProject.id;
      }
      mixpanel.registerSuperProperties(props);
    }
  }, [mixpanel, selectedTeam, selectedProject]);

  return (
    <Provider
      value={{
        headerTitle,
        setHeaderTitle,
        selectedProject,
        setSelectedProject,
        showProjectColorAndIconModal,
        setShowProjectColorAndIconModal,
        selectedTeam,
        setSelectedTeam,
        showPending,
        setShowPending,
      }}>
      {children}
    </Provider>
  );
};

export const useWebDrawerNavigator = (): WebDrawerNavigatorContext => {
  const context = useContext(webDrawerNavigatorContext);

  if (context === undefined) {
    throw new Error('useWebDrawerNavigator must be used within a Provider');
  }
  return context;
};
